import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Mic, MicOff, AlertCircle } from 'lucide-react';

interface VoiceRecorderProps {
  isRecording: boolean;
  setIsRecording: (recording: boolean) => void;
  onTranscript: (transcript: string) => void;
}

// Simpler, more reliable Web Speech API implementation
const VoiceRecorder: React.FC<VoiceRecorderProps> = ({ isRecording, setIsRecording, onTranscript }) => {
  const [isSupported, setIsSupported] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  useEffect(() => {
    const SpeechRecognitionCtor: any = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionCtor) {
      setIsSupported(false);
      return;
    }
    setIsSupported(true);
    const recognition: SpeechRecognition = new SpeechRecognitionCtor();
    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onresult = (event: any) => {
      try {
        const result = event.results?.[0]?.[0]?.transcript || '';
        if (result.trim()) onTranscript(result.trim());
      } catch {}
    };
    recognition.onerror = () => {
      setError('Voice input error. Please try again.');
      setIsRecording(false);
    };
    recognition.onend = () => {
      setIsRecording(false);
    };

    recognitionRef.current = recognition;
    return () => {
      try { recognition.stop(); } catch {}
    };
  }, [onTranscript, setIsRecording]);

  const toggleRecording = () => {
    if (isRecording) {
      try { recognitionRef.current?.stop(); } catch {}
      setIsRecording(false);
      return;
    }
    if (!isSupported) {
      setError('Voice input not supported in this browser.');
      return;
    }
    setError(null);
    try {
      recognitionRef.current?.start();
      setIsRecording(true);
    } catch (e) {
      setError('Unable to start voice input.');
      setIsRecording(false);
    }
  };

  return (
    <div className="relative">
      {error && (
        <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-3 py-1.5 rounded-md text-xs whitespace-nowrap z-20 shadow-lg border border-red-400">
          <div className="inline-flex items-center space-x-1">
            <AlertCircle className="w-3.5 h-3.5" />
            <span>{error}</span>
          </div>
        </div>
      )}
      <motion.button
        whileHover={{ scale: 1.05, y: -2 }}
        whileTap={{ scale: 0.95 }}
        onClick={toggleRecording}
        className={`voice-button p-4 rounded-2xl transition-all duration-200 shadow-lg ${
          isRecording
            ? 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white voice-recording'
            : isSupported === false
            ? 'bg-modern-400 cursor-not-allowed'
            : 'bg-modern-700/50 hover:bg-modern-700/70 text-white border border-modern-600/50'
        }`}
        disabled={isSupported === false}
        title={isSupported === false ? 'Voice input not supported in this browser' : 'Click to start voice recording'}
      >
        {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
      </motion.button>
    </div>
  );
};

export default VoiceRecorder;